import { SavingsOutlined } from '@mui/icons-material';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { numericFormatter } from 'react-number-format';
import { useToast } from '../../../Hooks/useToast';
import { useUser } from '../../../Hooks/useUser';
import {
  useGetDefaultPaymentMethodQuery,
  useGetPaymentHistoryQuery,
  useLazyCreateSetupIntentQuery,
} from '../../../Redux/api/stripeApi';
import { setShowAddPaymentMethodModal } from '../../../Redux/slices/GlobalModalSlice';
import {
  setClientSecret,
  setCustomerSessionClientSecret,
} from '../../../Redux/slices/GlobalSlice';
import { useAppDispatch, useAppSelector } from '../../../Redux/store';
import { KaziCard } from '../../../Shared Components/Kazi/Cards/KaziCard';
import { KaziWallet } from '../../../Shared Components/Kazi/KaziWallet';
import { RemoveCardConfirmModal } from './RemoveCardConfirmModal';

interface IProps {}

export const Transactions = () => {
  /* ******************** Hooks ******************** */
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useUser();
  const { userId, userType } = useAppSelector(state => state.Global);
  const { data: defaultPaymentMethod } = useGetDefaultPaymentMethodQuery();
  const card = defaultPaymentMethod?.card;
  const [createSetupIntent] = useLazyCreateSetupIntentQuery();
  //@ts-ignore
  const { data: paymentHistory, isLoading: paymentHistoryLoading } =
    useGetPaymentHistoryQuery(user?.stripe?.customer_id ?? skipToken);
  /* ******************** Variables ******************** */
  const isClient = userType === 'client';
  const walletCard = {
    title: '$0 in wallet',
    subtitle: '0 transactions',
    icon: <SavingsOutlined />,
    button: 'Transfer to bank',
    gridRow: '1',
    gridColumn: '3',
    marginTop: 0,
    action: () => {},
  };
  const [removeOpen, setRemoveOpen] = useState<boolean>(false);
  /* ******************** Functions ******************** */
  const handlePaymentEdit = () => {
    createSetupIntent(userId!)
      .unwrap()
      .then(res => {
        dispatch(setClientSecret(res.clientSecret));
        dispatch(setCustomerSessionClientSecret(res.clientSecret));
        dispatch(setShowAddPaymentMethodModal(true));
      })
      .catch(err => {
        console.error('Error occured while creating setup intent: ', err);
        showToast({
          text: 'Unable to modify payment method',
          variant: 'error',
        });
      });
  };

  const handleRemoveOpenClick = () => {
    setRemoveOpen(!removeOpen);
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  return (
    <Grid
      display={'grid'}
      gridTemplateColumns={'1fr auto'}
      gap={4}
      marginTop={1}
    >
      <Grid display={'flex'} flexDirection={'column'} gap={2}>
        <Grid>
          {paymentHistory && paymentHistory.length > 0 ? (
            <Grid>
              {paymentHistory.map((transaction, idx) => (
                <>
                  <Box
                    key={`${transaction.description}-${idx}`}
                    display={'flex'}
                    flexDirection={'row'}
                    marginBottom={2}
                  >
                    <Box display={'flex'} flexDirection={'column'} flex={1}>
                      <Typography variant="body-medium">
                        {transaction.description}
                      </Typography>
                      <Typography variant="body-smaller">
                        {transaction.created}
                      </Typography>
                    </Box>
                    <Box
                      display={'flex'}
                      flex={1}
                      justifyContent={'flex-end'}
                      alignItems={'center'}
                    >
                      <Typography variant="label-small" fontWeight={'bold'}>
                        {numericFormatter(transaction.amount.toString(), {
                          prefix: '$',
                          thousandSeparator: ',',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                </>
              ))}
            </Grid>
          ) : (
            <Typography variant="body-large" sx={{ color: 'black.300' }}>
              {' '}
              Your transactions will appear here when you begin{' '}
              {isClient ? 'making payments' : 'getting payments'} for short-term
              projects.
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid marginRight={8} marginTop={4}>
        <KaziWallet
          sx={{
            marginBottom: 3.5,
            '&.Mui-disabled': {
              backgroundColor: 'black.50',
              border: 'none',
              color: 'primary.main',
            },
            width: '308px',
          }}
        />
        <KaziCard
          title={card?.brand.toUpperCase()}
          subtitle={`***${card?.last4}`}
          subtitle2={`${card?.exp_month}/${String(card?.exp_year).substring(
            2,
          )} expiration`}
          button={{ label: 'Edit', action: handlePaymentEdit }}
          secondaryButton={{
            label: 'Remove',
            action: handleRemoveOpenClick,
          }}
        />
      </Grid>
      <RemoveCardConfirmModal
        open={removeOpen}
        handleClose={handleRemoveOpenClick}
      />
    </Grid>
  );
};
